import { useEffect, useState } from "react";
import { api, fmtINR, formatError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Infinity as InfinityIcon, TrendingUp, Loader2 } from "lucide-react";

const loadRazorpay = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Failed to load Razorpay checkout"));
    document.body.appendChild(s);
  });

export default function StoreSubscription() {
  const [store, setStore] = useState(null);
  const [plans, setPlans] = useState([]);
  const [trial, setTrial] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => Promise.all([
    api.get("/store/me"),
    api.get("/store/plans").catch(() => ({ data: [] })),
    api.get("/store/trial-status").catch(() => ({ data: null })),
  ]).then(([s, p, t]) => { setStore(s.data); setPlans(p.data); setTrial(t.data); });

  useEffect(() => { load(); }, []);

  const startUpgrade = async (plan) => {
    if (!store) return;
    setBusy(plan.id);
    try {
      const { data } = await api.post("/store/subscription/create-payment-order", { plan_id: plan.id });
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: data.razorpay_key,
        amount: data.amount_paise,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "Print Plus by AI Upaya",
        description: `Subscription — ${data.plan_name}`,
        image: "/logo.png",
        theme: { color: "#F97316" },
        prefill: {
          name: store.owner_name || "",
          email: store.owner_email || "",
          contact: store.mobile || "",
        },
        notes: { tenant_id: store.id, plan_id: plan.id },
        handler: async (res) => {
          try {
            await api.post("/store/subscription/verify-payment", {
              plan_id: plan.id,
              razorpay_order_id: data.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success(`Upgraded to ${plan.name} — welcome aboard!`);
            await load();
          } catch (e) {
            toast.error(formatError(e));
          } finally {
            setBusy("");
          }
        },
        modal: {
          ondismiss: () => {
            setBusy("");
            toast.info("Payment cancelled");
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        setBusy("");
        toast.error(resp?.error?.description || "Payment failed");
      });
      rzp.open();
    } catch (e) {
      setBusy("");
      toast.error(formatError(e));
    }
  };

  if (!store) return null;
  const sub = store.subscription;
  const isTrial = !!trial?.is_trial;
  const usedPct = isTrial && trial.daily_limit ? Math.min(100, Math.round((trial.used_today / trial.daily_limit) * 100)) : 0;

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="store-sub-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Subscription</h1>
        <p className="text-slate-500">Free forever · upgrade when you outgrow 10 prints/day</p>
      </div>

      {isTrial && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-white max-w-4xl" data-testid="trial-banner">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700 font-bold">Free Trial · Lifetime</span>
                </div>
                <div className="font-display text-xl font-bold text-slate-900">You're on the free-forever plan</div>
                <div className="text-sm text-slate-600 mt-0.5">10 prints per day, every day, at zero cost. Counter resets at midnight IST.</div>
              </div>
              <div className="sm:w-48">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 font-semibold">Today</span>
                  <span className="font-mono text-sm font-bold" data-testid="trial-usage">{trial.used_today} / {trial.daily_limit}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${usedPct >= 100 ? "bg-red-500" : usedPct >= 80 ? "bg-orange-500" : "bg-emerald-500"}`}
                    style={{ width: `${usedPct}%` }}
                  />
                </div>
                {trial.remaining_today === 0 && (
                  <div className="text-[11px] text-red-600 font-semibold mt-1.5">Daily limit reached — upgrade to keep printing</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 max-w-4xl">
        <CardHeader><CardTitle className="font-display flex items-center gap-2">Current plan {isTrial && <Badge className="bg-amber-500">Free Trial</Badge>}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {sub ? <>
            <div className="flex justify-between"><span className="text-slate-500">Plan</span><span className="font-semibold" data-testid="current-plan-name">{sub.plan_name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-mono">{isTrial ? <span className="inline-flex items-center gap-1">Free <InfinityIcon className="w-3.5 h-3.5" /></span> : fmtINR(sub.amount)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{isTrial ? "Renews" : "Expires"}</span><span className="font-mono">{isTrial ? "Never — lifetime" : (sub.expiry_date || "").slice(0, 10)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Status</span><Badge className={store.subscription_active ? "bg-emerald-500" : "bg-red-500"}>{store.subscription_active ? "Active" : "Expired"}</Badge></div>
          </> : <p className="text-slate-500">No active subscription.</p>}
        </CardContent>
      </Card>

      {plans.length > 0 && (
        <>
          <div className="flex items-center gap-2 pt-2 max-w-4xl">
            <TrendingUp className="w-5 h-5 text-orange-500" />
            <h2 className="font-display text-xl font-bold">{isTrial ? "Upgrade — remove the daily cap" : "Renew / upgrade"}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 max-w-4xl">
            {plans.map((p, idx) => (
              <Card key={p.id} className={`border-slate-200 relative ${idx === 1 ? "ring-2 ring-orange-500 shadow-lg" : ""}`}>
                {idx === 1 && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[10px] font-mono uppercase tracking-widest font-bold px-2.5 py-0.5 rounded-full">Popular</div>}
                <CardContent className="p-5 space-y-3">
                  <div className="text-xs uppercase tracking-widest text-orange-600 font-semibold">{p.name}</div>
                  <div className="font-display text-3xl font-black">{fmtINR(p.price)}<span className="text-sm text-slate-400 font-normal">/{p.duration_days}d</span></div>
                  {p.description && <div className="text-xs text-slate-500">{p.description}</div>}
                  <ul className="space-y-1.5 text-sm">{(p.features || []).map((f, i) => <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /><span>{f}</span></li>)}</ul>
                  <Button
                    onClick={() => startUpgrade(p)}
                    disabled={busy === p.id}
                    className={`w-full ${idx === 1 ? "bg-orange-500 hover:bg-orange-600" : "bg-slate-900 hover:bg-slate-800"}`}
                    data-testid={`upgrade-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {busy === p.id ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Opening Razorpay…</> : <><Zap className="w-3.5 h-3.5 mr-1.5" />{isTrial ? `Upgrade to ${p.name}` : `Renew with ${p.name}`}</>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-[11px] text-slate-400 max-w-4xl">Payments are processed securely via Razorpay. Upgrades take effect immediately after successful payment. Cancel anytime — you'll continue on your paid plan until it expires, then automatically fall back to the Free Trial.</div>
        </>
      )}
    </div>
  );
}
